/** @jsxImportSource @emotion/react */
import tw from "twin.macro";

import { css } from "@emotion/css";
import {
  useObservable,
  useObservableState,
  useSubscription,
} from "observable-hooks";
import * as R from "ramda";
import React, { FC, memo, useState } from "react";
import { combineLatest, Observable } from "rxjs";
import { map } from "rxjs/operators";
import { Note } from "../state/data";
import {
  noteCreator,
  notesStore,
  useAreaOrder,
  useNotes,
  useNotesOperations,
} from "../state/state/store";
import { dbg } from "../utils";
import {
  animationEndings$,
  dragStarts$,
  isDraggingOrAnimating$,
  lastDraggedNoteId$,
  ongoingDrag$,
} from "./drag/Control";
import { DraggableNote } from "./drag/DraggableNote";
import { Placeholder } from "./drag/Placeholder";
import { useRects } from "./useRects";
import firebase from "firebase/app";
import { GrAddCircle } from "react-icons/gr";
import { animated, to, useSpring } from "react-spring";

const padding = 7;

export const NoteList: FC<{
  selector: (n: Note) => boolean;
  effector: (n: Note) => Note;
  id: string;
}> = memo(({ id, selector, effector, children }) => {
  // console.count("NoteList render " + id);
  const { notes } = useNotes();
  const { order } = useAreaOrder(id);
  const computedNoteIds = notes.filter(selector).map((n) => n.id);
  const noteIds = [
    ...order.filter((id) => R.includes(id, computedNoteIds)),
    ...computedNoteIds.filter((id) => !R.includes(id, order)),
  ];
  return (
    <InnerNoteList id={id} noteIds={noteIds} effector={effector}>
      {children}
    </InnerNoteList>
  );
});

const CreationIndicator = () => {
  const { color } = useSpring({
    from: { color: "white" },
    color: "rgba(21, 101, 192, 0.239)",
    delay: 100,
  });

  return (
    <div
      tw="h-1 p-2 flex w-full relative"
      className={css`
        /* top: 2px; */
      `}
    >
      <animated.div
        tw="h-0.5 my-auto w-full flex"
        className={css`
          transition: 1s ease-in all;
        `}
        style={{
          //@ts-ignore
          backgroundColor: color.to((o) => o),
        }}
      ></animated.div>
    </div>
  );
};

const InnerNoteList: FC<{
  effector: (n: Note) => Note;
  noteIds: string[];
  id: string;
}> = ({ id, noteIds, effector, children }) => {
  const notesOperations = useNotesOperations();
  const [createdIdx, setCreatedIdx] = useState<number | undefined>(undefined);

  const [transientOrder, setTransientOrder] = useState<string[] | undefined>(
    undefined
  );
  const [animating, setAnimating] = useState(true);

  const [rectRefs, noteNodes] = useRects();

  const lastDraggedNoteId = useObservableState(lastDraggedNoteId$);

  const ongoingDrag = useObservableState(ongoingDrag$);
  const lastDragStart = useObservableState(dragStarts$);
  const placeholderHeight = lastDragStart?.position.height ?? 50;

  useSubscription(animationEndings$, (e) => {
    if (transientOrder === undefined) {
      // drop not in component
      return;
    }
    console.log("setting order");
    notesStore.setOrderFor(
      id,
      R.update(
        R.indexOf("placeholder", transientOrder!),
        lastDraggedNoteId!,
        transientOrder!
      )
    );
    setAnimating(false);
    setTimeout(() => setAnimating(true), 300);
    setTransientOrder(undefined);
    notesOperations.updateNoteById(e.id, effector);
  });

  let tops = {};
  if (transientOrder) {
    const temp = {};
    R.drop(transientOrder.indexOf("placeholder") + 1, transientOrder).forEach(
      (id) => (temp[id] = placeholderHeight + 2 * padding)
    );
    tops = temp;
  } else if (createdIdx !== undefined) {
    // const temp = {};
    // // R.drop(createdIdx, noteIds).forEach((id) => (temp[id] = 10));
    // tops = temp;
  }

  const placeholderTop =
    transientOrder &&
    R.sum(
      R.take(R.indexOf("placeholder", transientOrder), transientOrder).map(
        (id) => noteNodes[id].getBoundingClientRect().height + 2 * padding
      )
    );

  const isDraggingOrAnimating = useObservableState(isDraggingOrAnimating$);

  return (
    <div
      className={css`
        height: 100%;
        position: relative;
        /* background-color: #e9ebee; */
      `}
      tw="py-2 px-1"
      onMouseMove={(e) => {
        if (ongoingDrag === undefined) {
          return;
        }

        const noteIdsWithoutDragged = R.reject(
          R.equals(lastDraggedNoteId),
          noteIds
        );
        const midpoints = R.map(
          (r) => r.y + r.height / 2,
          noteIdsWithoutDragged.map((id) =>
            noteNodes[id].getBoundingClientRect()
          )
        );
        const idx = midpoints.filter((y) => y < e.clientY).length;
        const newOrder = R.insert(idx, "placeholder", noteIdsWithoutDragged);
        if (!R.equals(newOrder, transientOrder)) {
          setTransientOrder(newOrder);
        }
      }}
      onMouseLeave={() => {
        setCreatedIdx(undefined);
        if (ongoingDrag === undefined) {
          return;
        }
        setTransientOrder(undefined);
      }}
    >
      {children}
      <div
        className={css`
          height: 100%;
          width: 100%;
          cursor: ${ongoingDrag !== undefined ? "grabbing" : "cell"};
          z-index: ${ongoingDrag !== undefined ? "30" : "10"};
        `}
        tw="inset-0 absolute"
        onClick={() => {
          const newnote = effector(noteCreator());
          notesOperations.addNote(newnote);
          notesStore.setOrderFor(
            id,
            R.insert(createdIdx!, newnote.id, noteIds)
          );
          setCreatedIdx(undefined);
          firebase.analytics().logEvent("note_created");
        }}
        onMouseMove={(e) => {
          const midpoints = R.map(
            (r) => r.y + r.height / 2,
            noteIds.map((id) => noteNodes[id].getBoundingClientRect())
          );
          const idx = midpoints.filter((y) => y < e.clientY).length;
          setCreatedIdx(idx);
        }}
      ></div>
      {transientOrder !== undefined && (
        <div
          key={"placeholder"}
          className={css`
            //notewrap
            width: 100%;
            padding: ${padding}px;
            padding-left: 10px;
            padding-right: 10px;
          `}
          style={{
            transform: `translate3d(0, ${placeholderTop}px, 0)`,
            position: "absolute",
          }}
        >
          <Placeholder height={placeholderHeight + "px"} />
        </div>
      )}
      {noteIds.map((noteId, i) => {
        return (
          <React.Fragment key={noteId}>
            {i === 0 && transientOrder === undefined && 0 === createdIdx && (
              <CreationIndicator />
            )}
            <div
              key={noteId}
              className={css`
                //notewrap
                width: 100%;
                padding: ${padding}px;
                padding-left: 10px;
                padding-right: 10px;
              `}
              style={{
                position: "relative",
                // Remove padding if it's being dragged
                paddingTop:
                  noteId === lastDraggedNoteId && isDraggingOrAnimating
                    ? 0
                    : padding,
                // Remove padding if it's being dragged
                paddingBottom:
                  noteId === lastDraggedNoteId && isDraggingOrAnimating
                    ? 0
                    : padding,
                transform:
                  tops[noteId] !== undefined
                    ? `translate(0,  ${tops[noteId]}px)`
                    : `none`,
                transition: animating
                  ? "400ms transform cubic-bezier(0.165, 0.84, 0.44, 1)"
                  : "0s all ease 0s",
              }}
            >
              <div ref={(r) => rectRefs(r, noteId)}>
                <DraggableNote id={noteId} />
              </div>
            </div>
            {transientOrder === undefined && i + 1 === createdIdx && (
              <CreationIndicator />
            )}
          </React.Fragment>
        );
      })}
      {transientOrder !== undefined && (
        <div
          className={"spacetaker"}
          style={{
            height: placeholderHeight,
          }}
        ></div>
      )}
      {noteIds.length === 0 && !transientOrder && (
        <div
          className={css`
            display: grid;
            place-items: center;
          `}
        >
          <span
            className={css`
              color: grey;
              font-weight: 300;
              margin-top: 1em;
              margin-bottom: 1em;
              width: 270px;
              text-align: center;
            `}
          >
            Drag and drop notes here to schedule them, or click to create a new
            one.
          </span>
        </div>
      )}
    </div>
  );
};
